import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { Spinner } from '@Components/Spinner';
import { fetchPendingAgentOrders, QUERY_KEY } from '../AgentOrderApproval/AgentOrderApproval';
import { PendingAgentOrder } from '../AgentOrderApproval/agent-order-approval-constants';
import { isApprovedAgentOrder } from '../AgentOrderApproval/agent-order-approval-utils';
import { AGENT_IDENTITY, GUARDRAILS, GuardrailControl, GuardrailKind } from './governance-config';
import {
  AGENTIC_GUARDRAILS_QUERY_KEY,
  applyLiveGuards,
  fetchLiveSendGuards,
  GuardrailTone,
  LiveBuyingPolicy,
  LiveSendGuards
} from './governance-live';

// Read-only view of the agentic-buying control plane, shown in the Buy step. It
// surfaces what is already enforced in the backend but otherwise invisible: the
// agent's governed identity, the hard guardrails (with where each is enforced),
// and a live lifecycle of the drafted orders showing the send being refused. The
// guardrail values mirror the enforced config (governance-config.ts, with
// provenance); the order lifecycle is live, from real order state.
interface GovernancePanelProps {
  company: string;
  // Resolved durable buying policy from the live governance.policy event (undefined
  // until the planner run streams it) — live-binds the allow-list / spend ceilings.
  livePolicy?: LiveBuyingPolicy;
}

export const GovernancePanel = ({ company, livePolicy }: GovernancePanelProps) => {
  const { data: orders, isLoading } = useQuery({
    queryKey: [QUERY_KEY, company],
    queryFn: () => fetchPendingAgentOrders(company),
    enabled: Boolean(company)
  });

  // Live send-guard state (kill-switch + media cap) from the deployed streaming
  // service. Undefined until it resolves, or if the read fails — the guardrails
  // section then falls back to the enforced-config values (never a lie).
  const { data: liveGuards } = useQuery({
    queryKey: [AGENTIC_GUARDRAILS_QUERY_KEY],
    queryFn: fetchLiveSendGuards
  });

  return (
    <div className='elevate-gov'>
      <div className='elevate-gov-head'>
        <h2 className='elevate-gov-title'>Governance &amp; Controls</h2>
        <p className='elevate-gov-sub'>
          Every action this agent takes runs under a governed identity with hard limits enforced in
          code — not in a policy document. This panel is read-only.
        </p>
      </div>
      <IdentitySection />
      <SeparationOfDutiesSection />
      <GuardrailsSection liveGuards={liveGuards} livePolicy={livePolicy} />
      <ActionLogSection orders={orders} loading={isLoading} />
    </div>
  );
};

const IdentitySection = () => (
  <section className='elevate-gov-section'>
    <h3 className='elevate-gov-section-title'>Agent identity &amp; scope</h3>
    <div className='elevate-gov-identity'>
      <div className='elevate-gov-id-row'>
        <span className='elevate-gov-id-key'>Identity</span>
        <span className='elevate-gov-id-val elevate-gov-mono'>{AGENT_IDENTITY.identity}</span>
      </div>
      <div className='elevate-gov-id-row'>
        <span className='elevate-gov-id-key'>Scope</span>
        <span className='elevate-gov-id-val'>{AGENT_IDENTITY.scope}</span>
      </div>
      <div className='elevate-gov-id-row'>
        <span className='elevate-gov-id-key'>Owner</span>
        <span className='elevate-gov-id-val'>{AGENT_IDENTITY.owner}</span>
      </div>
    </div>
  </section>
);

// Separation of duties is enforced server-side (bpm-lambdas changeOrderStatus
// returns 403 if the automation identity tries to commit an order it drafted) but
// is otherwise invisible in the UI. Surface it as an enforced structural control.
const SeparationOfDutiesSection = () => (
  <section className='elevate-gov-section'>
    <h3 className='elevate-gov-section-title'>Separation of duties</h3>
    <div className='elevate-gov-sod'>
      <p className='elevate-gov-sod-lead'>
        The agent drafts orders — it is structurally barred from approving or committing its own
        work.
      </p>
      <p className='elevate-gov-sod-detail'>
        If the automation identity tries to commit an order it drafted, the change is refused
        server-side with a 403. A different, named human must review and commit it. This is enforced
        in code, not by policy — the agent cannot sign off on its own order under any circumstances.
      </p>
      <p className='elevate-gov-provenance elevate-gov-mono'>
        AGENT_IDENTITY: automation@tinuiti.com · enforced by bpm-lambdas — orders.js
        (changeOrderStatus → 403)
      </p>
    </div>
  </section>
);

interface GuardrailsSectionProps {
  liveGuards: LiveSendGuards | undefined;
  livePolicy: LiveBuyingPolicy | undefined;
}

const DURABLE = GUARDRAILS.filter(g => g.kind === GuardrailKind.DURABLE);
const ROLLOUT = GUARDRAILS.filter(g => g.kind === GuardrailKind.ROLLOUT);

// Two groups on purpose: the durable guardrails are the governance that stays in
// force in production (approved inventory + spend discipline) — the headline. The
// rollout-safety controls are temporary pre-launch failsafes, shown as honest
// context, not the story.
const GuardrailsSection = ({ liveGuards, livePolicy }: GuardrailsSectionProps) => (
  <>
    <section className='elevate-gov-section'>
      <h3 className='elevate-gov-section-title'>Guardrails that govern the agent in production</h3>
      <p className='elevate-gov-groupnote'>
        These stay in force after launch — the agent operates inside them by design.
      </p>
      <div className='elevate-gov-guardrails'>
        {DURABLE.map(g => (
          <GuardrailCard
            key={g.envVar}
            guardrail={g}
            liveGuards={liveGuards}
            livePolicy={livePolicy}
          />
        ))}
      </div>
    </section>
    <section className='elevate-gov-section'>
      <h3 className='elevate-gov-section-title'>Rollout safety — temporary, removed at launch</h3>
      <p className='elevate-gov-groupnote'>
        Pre-launch failsafes that keep the tool from going too far before it&apos;s validated. Not
        the long-term governance model — they come off at go-live.
      </p>
      <div className='elevate-gov-guardrails'>
        {ROLLOUT.map(g => (
          <GuardrailCard
            key={g.envVar}
            guardrail={g}
            liveGuards={liveGuards}
            livePolicy={livePolicy}
          />
        ))}
      </div>
    </section>
  </>
);

interface GuardrailCardProps {
  guardrail: GuardrailControl;
  liveGuards: LiveSendGuards | undefined;
  livePolicy: LiveBuyingPolicy | undefined;
}

const GuardrailCard = ({ guardrail, liveGuards, livePolicy }: GuardrailCardProps) => {
  const { value, isLive, tone } = applyLiveGuards(guardrail, liveGuards, livePolicy);
  return (
    <div className='elevate-gov-guardrail'>
      <div className='elevate-gov-guardrail-head'>
        <span className='elevate-gov-guardrail-label'>{guardrail.label}</span>
        {/* The safe/live colour only applies to a CONFIRMED live reading. When the
            value is a config fallback (the live source has not streamed this session),
            it renders in a muted "default" badge — never dressed up in the green/danger
            state colours that would make a hardcoded number read as a live fact. */}
        <span
          className={cn('elevate-gov-badge', {
            'elevate-gov-badge--safe': isLive && tone === GuardrailTone.SAFE,
            'elevate-gov-badge--live': isLive && tone === GuardrailTone.LIVE,
            'elevate-gov-badge--default': !isLive
          })}
        >
          {value}
        </span>
      </div>
      {isLive ? undefined : (
        <p className='elevate-gov-notlive'>
          default · not live — run a plan to load the live value
        </p>
      )}
      <p className='elevate-gov-guardrail-prevents'>{guardrail.prevents}</p>
      <p className='elevate-gov-provenance elevate-gov-mono'>
        {guardrail.envVar} · {guardrail.enforcedAt}
        {isLive ? <span className='elevate-gov-livetag'> · live</span> : undefined}
      </p>
    </div>
  );
};

type StageState = 'done' | 'current' | 'pending' | 'blocked';

const LIFECYCLE: { key: string; label: string }[] = [
  { key: 'drafted', label: 'Drafted' },
  { key: 'held', label: 'Held for review' },
  { key: 'approved', label: 'Approved' },
  { key: 'send', label: 'Send — refused, enforced in code' }
];

const stageState = (order: PendingAgentOrder, key: string): StageState => {
  if (key === 'send') return 'blocked';
  if (key === 'drafted') return 'done';
  const approved = isApprovedAgentOrder(order);
  if (key === 'held') return approved ? 'done' : 'current';
  return approved ? 'current' : 'pending';
};

interface ActionLogSectionProps {
  orders: PendingAgentOrder[] | undefined;
  loading: boolean;
}

const ActionLogSection = ({ orders, loading }: ActionLogSectionProps) => (
  <section className='elevate-gov-section'>
    <h3 className='elevate-gov-section-title'>Action log — live order lifecycle</h3>
    <ActionLogBody orders={orders} loading={loading} />
  </section>
);

const ActionLogBody = ({ orders, loading }: ActionLogSectionProps) => {
  if (loading) return <Spinner />;
  if (!orders || orders.length === 0) {
    return (
      <p className='elevate-gov-empty'>
        No agent orders in flight. Draft one in the buy step to see the governed lifecycle here.
      </p>
    );
  }
  return (
    <div className='elevate-gov-log'>
      {orders.map(order => (
        <div key={order.id} className='elevate-gov-order'>
          <div className='elevate-gov-order-head'>
            Order #{order.id} · {order.network}
          </div>
          <ol className='elevate-gov-stages'>
            {LIFECYCLE.map(stage => {
              const state = stageState(order, stage.key);
              return (
                <li key={stage.key} className={cn('elevate-gov-stage', `is-${state}`)}>
                  <span className='elevate-gov-stage-dot' />
                  <span className='elevate-gov-stage-label'>{stage.label}</span>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
};
